import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PaginationControls from '../../components/PaginationControls';
import { getAllClients, deleteClients, updateClients, createClients } from '../../API/Crud/Clients';
import ConfirmationModal from '../../components/ConfirmationModal';
import DashboardTemplate from '../template/DashboardTemplate';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import { get, post, upload } from '../../services/ApiRouting';
import { useDropzone } from "react-dropzone";
import { BiEdit, BiTrash } from 'react-icons/bi';

function Clients() {
    const [clients, setClients] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [token, setToken] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [selectedClients, setSelectedClients] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 5,
        total: 0,
        lastPage: 1,
    });
    const { register, handleSubmit, control, reset, formState: { errors }, clearErrors } = useForm({
        defaultValues: {
            name: "",
            file: "",
        }
    });
    const [showConfirmModal, setShowConfirmModal] = useState(false); // State to control confirmation modal visibility
    const [ClientsToDelete, setClientsToDelete] = useState(null); // State to hold the Clients to be deleted

    // Pagination logic
    const totalClients = Clients.length;
    const indexOfLastItem = totalClients - (currentPage - 1) * itemsPerPage;
    const indexOfFirstItem = Math.max(indexOfLastItem - itemsPerPage, 0);
    // const currentClients = Clients.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => fetchClients(pageNumber);

    useEffect(() => {
        const storedToken = sessionStorage.getItem('token');
        if (storedToken) {
            setToken(storedToken);
        } else {
            console.error('No token found');
            setError('No token found. Please log in.');
        }
    }, []);

    async function fetchClients(number) {
        setLoading(true);
        try {
            const data = await getAllClients(number);
            setClients(data.data);
            console.log(data)
            setPagination({
                currentPage: data.data.current_page,
                perPage: data.data.per_page,
                total: data.data.total,
                lastPage: data.data.last_page,
            })
            setError(null);
        } catch (error) {
            setError(error.message || 'An unknown error occurred');
            toast.error('Failed to load Clients');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!token) return;
        fetchClients(1);
    }, [token]);


    const handleDeleteClients = async (ClientsId) => {
        try {
            const response = await deleteClients(ClientsId);
            if (response.ok) {

                setClients((prevClients) => ({
                    ...prevClients, // keep pagination info
                    data: prevClients.data.filter(service => service.id !== ClientsId)
                }));
                toast.success('Clients deleted successfully');
                reset()
                reset({}, { keepErrors: false, keepDirty: false, keepTouched: false });
            }
        } catch (error) {
            setError('Error deleting Clients');
            toast.error('Failed to delete Clients');
        }
    };

    useEffect(() => {
        if (selectedClients) {
            reset({
                name: selectedClients.name || "",
                file: selectedClients.image || [],
            });
        }
    }, [selectedClients, reset]);

    const handleEditClients = (Clients) => {
        setSelectedClients(Clients); // triggers useEffect above
        setFormData({
            id: Clients.id,
            name: Clients.name,
            file: Clients.image || [],
        });
        let imagePath = [];
        try {
            const parsed = JSON.parse(Clients.image);
            imagePath = parsed;          // take first element
        } catch (e) {
            console.error("Invalid JSON:", e);
        }
        setUploadedFiles(imagePath);
        setShowEditModal(true);
        setFormData(formData);
    };

    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [formData, setFormData] = useState({
        id: null,
        name: '',
        file: '',
    });
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    // Upload function
    const uploadFiles = async (files, field) => {
        setIsUploading(true);
        const uploadPromises = files.map(async (file) => {
            try {
                const formData = new FormData();
                formData.append('file', file, file.name);

                const response = await upload(`${API_URL}/crud/certs/fileUpload`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                const serverFilePath = response.fileId || response.data.file_path;
                return serverFilePath;
            } catch (err) {
                console.error("Upload error:", err);
                return null;
            }
        });

        const serverIds = await Promise.all(uploadPromises);
        const validServerIds = serverIds.filter(Boolean);

        // Update both local state and form field
        if (uploadedFiles && uploadedFiles.length > 0) {
            setUploadedFiles(prev => [...(prev.data || []), ...validServerIds]);

            // Update the form field value
            const currentValue = field.value || [];
            field.onChange([...currentValue, ...validServerIds]);

            setIsUploading(false);
        } else {
            setUploadedFiles(validServerIds);
            // Update the form field value
            field.onChange(validServerIds);
            setIsUploading(false);
        }
    };

    // Remove file function
    const removeFile = (index, field) => {
        setUploadedFiles([]);
        // field.onChange(newFiles);
    };


    const onCreateSubmit = async (data) => {
        const serviceData = {
            ...data,
            file: uploadedFiles, // array of server paths
        };
        try {
            const response = await createClients(serviceData);  // pass token + form data
            if (response.ok) {
                setShowModal(false);
                toast.success('Clients added successfully');
                //  setClients((prevClients) => console.log(prevClients));
                //  setClients((prevClients) => [...prevClients, response.data]);
                setClients(prev => ({
                    ...prev,
                    data: [...(prev.data || []), response.data]
                }));

                reset()
                reset({}, { keepErrors: false, keepDirty: false, keepTouched: false });
                setUploadedFiles([])
            }
        } catch (error) {
            console.error("Error creating Clients:", error);
        }
    };

    const onUpdateSubmit = async (data) => {
        const ClientsData = {
            ...data,
            file: uploadedFiles, // array of server paths
        };

        try {
            const response = await updateClients(selectedClients.id, ClientsData);  // pass token + form data
            if (response.status) {
                setShowEditModal(false);
                toast.success(response.message || 'Clients updated successfully');
                setClients((prevClients) => ({
                    ...prevClients, // keep pagination info
                    data: prevClients.data.map(service => service.id === selectedClients.id ? response.data : service)
                }));
                setSelectedClients(null);
            }
        } catch (error) {
            console.error("Error creating Clients:", error);
        }
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    };
    const baseUrl = `${API_PUBLIC_URL}`;


    const handleConfirmDelete = () => {
        if (ClientsToDelete) {
            handleDeleteClients(ClientsToDelete.id);
            setShowConfirmModal(false);
            setClientsToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmModal(false);
        setClientsToDelete(null);
        reset()
        reset({}, { keepErrors: false, keepDirty: false, keepTouched: false });
    };
    const handleCloseModal = () => {
        setShowModal(false);
        reset()
        reset({}, { keepErrors: false, keepDirty: false, keepTouched: false });
        setUploadedFiles([])
    };



    return (
        <>
            <DashboardTemplate>
                <div className="flex flex-col">
                    <ToastContainer />
                    <div className="transition-all duration-500 flex-1">
                        <div className="content p-2 md:p-5">

                            <div className="my-4 mx-2">
                                {loading ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-10 animate-spin fill-orange-600 block mx-auto"
                                        viewBox="0 0 24 24">
                                        <path
                                            d="M12 22c5.421 0 10-4.579 10-10h-2c0 4.337-3.663 8-8 8s-8-3.663-8-8c0-4.336 3.663-8 8-8V2C6.579 2 2 6.58 2 12c0 5.421 4.579 10 10 10z"
                                            data-original="#000000" />
                                    </svg>
                                ) : error ? (
                                    <p>Error: {error}</p>
                                ) : (
                                    <div className="font-[sans-serif]">
                                        <div className="flex justify-between items-center mb-4">
                                            <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
                                            <button className="bg-[#F2A549] font-normal text-black px-4 py-2 cursor-pointer rounded hover:bg-[#efb46f]" onClick={() => setShowModal(true)}>
                                                Add New Client
                                            </button>
                                        </div>
                                        <div className='w-auto max-w-full overflow-x-auto'>
                                            <table className="border-collapse min-w-full border border-gray-300 rounded-lg">
                                                <thead className="bg-[#F2A549] text-white border-0">
                                                    <tr className='text-center'>
                                                        <th className="p-4  font-normal text-black">Image</th>
                                                        <th className="p-4  font-normal text-black">Name</th>
                                                        <th className="p-4  font-normal text-black">Date</th>
                                                        <th className="p-4  font-normal text-black">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {Array.isArray(clients.data) && clients.data.map((client) => {
                                                        let imagePath = "";
                                                        try {
                                                            const parsed = JSON.parse(client.image); // parsed = ["/storage/uploads/services/..."]
                                                            imagePath = parsed;          // take first element
                                                        } catch (e) {
                                                            console.error("Invalid JSON:", e);
                                                        }
                                                        return (
                                                            <tr className=" border border-gray-300" key={client.id}>
                                                                <td className=" border border-gray-300 py-2 ">

                                                                    {imagePath ?
                                                                        <img src={baseUrl + imagePath} alt={client.name} className="w-20 h-20 mx-auto object-cover rounded-full" />
                                                                        :
                                                                        <img src="/images/gecam.png" alt={client.name} className="w-20 h-20 mx-auto object-cover rounded-full" />
                                                                    }
                                                                </td>
                                                                <td className=" text-center text-sm border border-gray-300">{client.name}</td>

                                                                <td className=" text-sm border border-gray-300 text-center">{formatDate(client.created_at)}</td>
                                                                <td className="border border-gray-300 text-center ">
                                                                    <div className='flex justify-center gap-4'>
                                                                        <button className="cursor-pointer" title="Edit" onClick={() => handleEditClients(client)}>
                                                                            <BiEdit size={25} />
                                                                        </button>
                                                                        <button className="cursor-pointer" title="Delete" onClick={() => {
                                                                            setClientsToDelete(client)
                                                                            setShowConfirmModal(true)
                                                                        }}>
                                                                            <BiTrash size={25} />

                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )
                                                    }).reverse()}
                                                    {(clients.length === 0 || clients.data.length === 0) && (
                                                        <tr>
                                                            <td colSpan="5" className="p-4 text-center text-gray-500 border border-gray-300">
                                                                No Clients available.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        <PaginationControls
                                            itemsPerPage={pagination.perPage}
                                            totalItems={pagination.total}
                                            currentPage={pagination.currentPage}
                                            paginate={paginate}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <ConfirmationModal
                        show={showConfirmModal}
                        title={ClientsToDelete ? ClientsToDelete.name : ''}
                        onConfirm={handleConfirmDelete}
                        onCancel={handleCancelDelete}
                    />
                </div>

                {/* Create Modal */}
                <Dialog open={showModal} onClose={setShowModal} className="relative z-[9999]">
                    <DialogBackdrop transition
                        className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
                    />

                    <div className="fixed inset-0 z-10 w-full overflow-y-auto">
                        <div className="flex min-h-screen items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <DialogPanel transition className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full max-w-[70%] data-closed:sm:translate-y-0 data-closed:sm:scale-95" >
                                <form onSubmit={handleSubmit(onCreateSubmit)}>
                                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                        <div className="sm:col-span-4 mb-3">
                                            <label htmlFor="name" className="block text-sm/6 font-medium text-gray-900">
                                                Name
                                            </label>
                                            <div className="mt-2">
                                                <input
                                                    id="name"
                                                    name="name"
                                                    type="text"
                                                    autoComplete="name"
                                                    {...register('name', {
                                                        required: 'Name is required',
                                                    })}
                                                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                                />
                                                {errors.name && (
                                                    <span className="error-message">
                                                        {errors.name.message}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="sm:col-span-4 mb-3">
                                            <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                                                Image
                                            </label>
                                            <div className="mt-2">
                                                <Controller
                                                    name="file"
                                                    control={control}
                                                    defaultValue={[]} // Initialize with empty array
                                                    rules={{
                                                        required: "Image is required",
                                                        validate: (value) => {
                                                            if (!value || value.length === 0) {
                                                                return "Au moins une image is requirede";
                                                            }
                                                            return true;
                                                        }
                                                    }}
                                                    render={({ field }) => {
                                                        const { getRootProps, getInputProps, isDragActive } = useDropzone({
                                                            accept: {
                                                                'image/jpeg': ['.jpeg', '.jpg'],
                                                                'image/png': ['.png']
                                                            },
                                                            multiple: false,
                                                            onDrop: (acceptedFiles) => {
                                                                if (acceptedFiles.length > 0) {
                                                                    uploadFiles(acceptedFiles, field);
                                                                }
                                                            }
                                                        });

                                                        return (
                                                            <>
                                                                <div {...getRootProps()} className="dropzone border-2 border-dashed border-gray-300 p-6 mb-4 cursor-pointer hover:border-gray-400 transition-colors">
                                                                    <input {...getInputProps()} />
                                                                    <div className="text-center">
                                                                        {isDragActive ? (
                                                                            <p className="text-blue-600">Drop the files here ...</p>
                                                                        ) : (
                                                                            <div>
                                                                                <p className="text-gray-600">Drag & drop images here, or click to select</p>
                                                                                <p className="text-sm text-gray-400 mt-1">Supports: JPG, JPEG, PNG</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {isUploading && (
                                                                    <div className="mb-4">
                                                                        <p className="text-blue-600">Uploading files...</p>
                                                                    </div>
                                                                )}

                                                                {uploadedFiles.length > 0 && (
                                                                    <div className="mb-4">
                                                                        <h4 className="font-medium mb-2">Uploaded Files:</h4>
                                                                        <ul className="space-y-2">
                                                                            {
                                                                                <li className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                                                                    <div className='flex items-center gap-2'>
                                                                                        <img width={100} src={baseUrl + uploadedFiles} className='rounded-full' />
                                                                                        {/* <span className="text-sm text-gray-700">{uploadedFiles}</span> */}
                                                                                    </div>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => removeFile(i, field)}
                                                                                        className="text-red-600 hover:text-red-800 text-sm cursor-pointer"
                                                                                    >
                                                                                        Remove
                                                                                    </button>
                                                                                </li>
                                                                            }
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    }}
                                                />

                                                {errors.file && (
                                                    <span className="error-message">
                                                        {errors.file.message}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                        <button type="submit" className="inline-flex w-full justify-center cursor-pointer rounded-md bg-[#F2A549] px-3 py-2 text-sm font-semibold text-black shadow-xs hover:bg-[#f1c40f] sm:ml-3 sm:w-auto" >
                                            Create
                                        </button>
                                        <button type="button" data-autofocus onClick={handleCloseModal} className="mt-3 cursor-pointer inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto" >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </DialogPanel>
                        </div>
                    </div>
                </Dialog>
                {/* Edit Modal */}
                <Dialog open={showEditModal} onClose={setShowEditModal} className="relative z-[9999]">
                    <DialogBackdrop
                        transition
                        className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
                    />

                    <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div className="flex min-h-screen items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <DialogPanel transition className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full max-w-[70%] data-closed:sm:translate-y-0 data-closed:sm:scale-95" >
                                <form onSubmit={handleSubmit(onUpdateSubmit)}>
                                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                        <div className="sm:col-span-4 mb-3">
                                            <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                                                Name
                                            </label>
                                            <div className="mt-2">
                                                <input
                                                    id="name"
                                                    name="name"
                                                    type="text"
                                                    autoComplete="name"
                                                    {...register('name', {
                                                        required: 'Name is required',
                                                    })}
                                                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                                />
                                                {errors.name && (
                                                    <span className="error-message">
                                                        {errors.name.message}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="sm:col-span-4 mb-3">
                                            <label htmlFor="images" className="block text-sm/6 font-medium text-gray-900">
                                                Image
                                            </label>
                                            <div className="mt-2">
                                                <Controller
                                                    name="file"
                                                    control={control}
                                                    defaultValue={[]} // Initialize with empty array
                                                    rules={{
                                                        required: "Image is required",
                                                        validate: (value) => {
                                                            if (!value || value.length === 0) {
                                                                return "Au moins une image is requirede";
                                                            }
                                                            return true;
                                                        }
                                                    }}
                                                    render={({ field }) => {
                                                        const { getRootProps, getInputProps, isDragActive } = useDropzone({
                                                            accept: {
                                                                'image/jpeg': ['.jpeg', '.jpg'],
                                                                'image/png': ['.png']
                                                            },
                                                            multiple: true,
                                                            onDrop: (acceptedFiles) => {
                                                                console.log("Files dropped:", acceptedFiles);
                                                                if (acceptedFiles.length > 0) {
                                                                    uploadFiles(acceptedFiles, field);
                                                                }
                                                            }
                                                        });

                                                        return (
                                                            <>
                                                                <div {...getRootProps()} className="dropzone border-2 border-dashed border-gray-300 p-6 mb-4 cursor-pointer hover:border-gray-400 transition-colors">
                                                                    <input {...getInputProps()} />
                                                                    <div className="text-center">
                                                                        {isDragActive ? (
                                                                            <p className="text-blue-600">Drop the files here ...</p>
                                                                        ) : (
                                                                            <div>
                                                                                <p className="text-gray-600">Drag & drop images here, or click to select</p>
                                                                                <p className="text-sm text-gray-400 mt-1">Supports: JPG, JPEG, PNG</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {isUploading && (
                                                                    <div className="mb-4">
                                                                        <p className="text-blue-600">Uploading files...</p>
                                                                    </div>
                                                                )}
                                                                {uploadedFiles !== null ? (

                                                                    <div className="mb-4">
                                                                        <h4 className="font-medium mb-2">Uploaded Files:</h4>
                                                                        <ul className="space-y-2">
                                                                            <li className="flex items-center justify-between bg-gray-50 p-2 rounded" >
                                                                                <div className="flex items-center gap-2">
                                                                                    <img
                                                                                        width={100}
                                                                                        src={baseUrl + uploadedFiles}
                                                                                        alt={selectedClients?.name || "Uploaded image"}
                                                                                        className="rounded-full"
                                                                                    />
                                                                                </div>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => removeFile(field)}
                                                                                    className="text-red-600 hover:text-red-800 text-sm cursor-pointer"
                                                                                >
                                                                                    Remove
                                                                                </button>
                                                                            </li>
                                                                        </ul>
                                                                    </div>
                                                                ) : 'No files uploaded yet.'}
                                                            </>
                                                        );
                                                    }}
                                                />

                                                {errors.file && (
                                                    <span className="error-message">
                                                        {errors.file.message}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                        <button
                                            type="submit"
                                            className="inline-flex cursor-pointer w-full justify-center rounded-md bg-[#F2A549]  px-3 py-2 text-sm font-semibold text-black shadow-xs hover:bg-[#efb46f] sm:ml-3 sm:w-auto"
                                        >
                                            Update
                                        </button>
                                        <button
                                            type="button"
                                            data-autofocus
                                            onClick={() => setShowEditModal(false)}
                                            className="mt-3 cursor-pointer inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </DialogPanel>
                        </div>
                    </div>
                </Dialog>
            </DashboardTemplate>
        </>
    );
}

export default Clients;
